import {
  Component,
  DestroyRef,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { User } from 'src/app/models/user';

import { FeatureFlagsEnum } from '../../core/constants/feature-flags.enum';
import { PostHogService } from '../../core/services/posthog.service';
import { SidebarService } from '../../core/services/sidebar.service';
import { Sidebar } from '../../core/utils/sidebar';
import { FlexComponent } from '../../ui-kit/flex/flex.component';
import { LogoComponent } from '../../ui-kit/logo/logo.component';
import { SidebarAccountMenuComponent } from './sidebar-account-menu/sidebar-account-menu.component';
import { SidebarButtonComponent } from './sidebar-button/sidebar-button.component';
import { SidebarSideMenuComponent } from './sidebar-side-menu/sidebar-side-menu.component';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SidebarButtonComponent,
    SidebarAccountMenuComponent,
    MatButtonModule,
    MatIconModule,
    SidebarSideMenuComponent,
    FlexComponent,
    LogoComponent,
  ],
})
export class SidebarComponent implements OnInit, OnDestroy {
  private sidebarService = inject(SidebarService);
  private elementRef = inject(ElementRef);
  private posthogService = inject(PostHogService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  @Input({ required: true }) user: User;

  sidebarHovered = false;
  collapseButtonHovered = false;
  sidebar: Sidebar;
  mobileMenuVisible = false;
  integrationsTabVisible = false;
  projectsTabVisible = false;

  ngOnInit(): void {
    this.sidebar = this.sidebarService.getSidebar();
    this.sidebar.setSidebarElementRef(this.elementRef);
    this.watchFeatureFlags();
    this.watchNavigationEnd();
  }

  ngOnDestroy(): void {
    this.sidebarService.destroySidebar();
  }

  toggleSidebarMenuHoverState(hovered: boolean): void {
    this.sidebarHovered = hovered;
  }

  toggleCollapseButtonHoverState(hovered: boolean): void {
    this.collapseButtonHovered = hovered;
  }

  toggleMobileMenu(): void {
    this.mobileMenuVisible = !this.mobileMenuVisible;
  }

  closeMobileMenu(): void {
    if (this.mobileMenuVisible) {
      this.mobileMenuVisible = false;
    }
  }

  private watchFeatureFlags(): void {
    this.posthogService
      .isFeatureEnabled(FeatureFlagsEnum.CRU_675_CASH_FLOW_FORECAST)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isCashFlowForecastEnabled) => {
        this.sidebarService.patchMappingData({ isCashFlowForecastEnabled });
      });

    this.posthogService
      .isFeatureEnabled(FeatureFlagsEnum.CRU_ASSET_LEDGER)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((financialsVisible) => {
        this.sidebarService.patchMappingData({ financialsVisible });
      });

    this.posthogService
      .isFeatureEnabled(FeatureFlagsEnum.CRU_882_PAYROLL_TAB)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isPayrollTabEnabled) => {
        this.sidebarService.patchMappingData({ isPayrollTabEnabled });
      });

    this.posthogService
      .isFeatureEnabled(FeatureFlagsEnum.CRU_1224_HIDE_CASHFLOW_TAB)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isCashflowTabHidden) => {
        this.sidebarService.patchMappingData({ isCashflowTabHidden });
      });

    this.posthogService
      .isFeatureEnabled(FeatureFlagsEnum.GROW_1242_INTEGRATIONS_PAGE)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isIntegrationsPageEnabled) => {
        this.integrationsTabVisible = isIntegrationsPageEnabled;
        this.sidebarService.patchMappingData({ isIntegrationsPageEnabled });
      });

    this.posthogService
      .isFeatureEnabled(FeatureFlagsEnum.CNP_1320_UNIFIED_PROJECTS_TAB)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isScProjectsEnabled) => {
        this.projectsTabVisible = isScProjectsEnabled;
        this.sidebarService.patchMappingData({ isScProjectsEnabled });
      });
  }

  private watchNavigationEnd(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        if (this.mobileMenuVisible) {
          this.mobileMenuVisible = false;
        }
      });
  }
}
